import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Eye, Settings2 } from 'lucide-react';
import { formBuilderApi } from './api';
import FieldEditor from './FieldEditor';
import FormRenderer from './FormRenderer';
import { FormField, FormSchema, FormStep, FormTag } from './types';
import { secondaryButton } from './ui';
import { ROUTES } from '../../config/links';
import {
  blank,
  materializeLegacyDynamicGroups,
  newField,
  newStep,
  normalizeSchema,
  remapCondition,
  remapFieldReferences,
  removeFieldReferences,
} from './editor/helpers';
import EditorHeader from './editor/EditorHeader';
import SetupSection from './editor/SetupSection';
import StepsSection from './editor/StepsSection';
import ReviewSection from './editor/ReviewSection';
import SubmissionSection from './editor/SubmissionSection';
import FieldListPanel from './editor/FieldListPanel';
import PublishDialog from './editor/PublishDialog';
import DuplicateDialog, { DuplicateSelection } from './editor/DuplicateDialog';
import EditorSkeleton from './editor/EditorSkeleton';

export default function FormBuilderEditorPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const editing = !!formId;
  // New forms arrive from the "New form" dialog with the sensor type + tag already chosen.
  const startState = useLocation().state as { sensor_type?: string; category?: string; tags?: FormTag[] } | null;
  const [sensorType, setSensorType] = useState<string | undefined>(startState?.sensor_type);
  const [category, setCategory] = useState<string | undefined>(startState?.category);
  const [tags, setTags] = useState<FormTag[]>(startState?.tags || []);
  const [name, setName] = useState('');
  const [persistedFormId, setPersistedFormId] = useState<string>();
  const [description, setDescription] = useState('');
  const [schema, setSchema] = useState<FormSchema>(blank());
  const [stepIndex, setStepIndex] = useState(0);
  const [fieldKey, setFieldKey] = useState<string>();
  const [preview, setPreview] = useState(false);
  const [previewValues, setPreviewValues] = useState<Record<string, unknown>>({});
  const [previewStep, setPreviewStep] = useState(0);
  const [loading, setLoading] = useState(editing);
  const [dragKey, setDragKey] = useState<string>();
  const [dragStepIndex, setDragStepIndex] = useState<number>();
  const [movingFieldKey, setMovingFieldKey] = useState<string>();
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [stepMenuIndex, setStepMenuIndex] = useState<number>();
  const [duplicating, setDuplicating] = useState(false);
  const missingSelection = !editing && !persistedFormId && (!startState?.sensor_type || !startState?.category || !startState?.tags?.length);
  const activeFormId = formId ? decodeURIComponent(formId) : persistedFormId;

  useEffect(() => {
    if (!editing) return;
    formBuilderApi
      .get(decodeURIComponent(formId!))
      .then((f) => {
        setName(f.name);
        setSensorType(f.sensor_type || undefined);
        setCategory(f.category || undefined);
        setTags(f.tags || []);
        setDescription(f.description || '');
        if (f.schema) setSchema(normalizeSchema(materializeLegacyDynamicGroups(f.schema)));
      })
      .catch((e) => toast.error(e?.message || 'Failed to load form'))
      .finally(() => setLoading(false));
  }, [editing, formId]);

  const step = schema.steps[stepIndex];
  const field = step?.fields.find((f) => f.key === fieldKey);
  const allKeys = useMemo(
    () =>
      schema.steps.flatMap((s) =>
        s.fields.map((f) => ({ key: f.key, label: f.label || f.key, type: f.type })),
      ),
    [schema],
  );
  const updateStep = (next: FormStep) =>
    setSchema((s) => ({
      ...s,
      steps: s.steps.map((x, i) => (i === stepIndex ? next : x)),
    }));

  const addStep = () => {
    const next = newStep(schema.steps.length + 1);
    setSchema((x) => ({ ...x, steps: [...x.steps, next] }));
    setStepIndex(schema.steps.length);
    setFieldKey(undefined);
  };

  const moveStep = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= schema.steps.length || to >= schema.steps.length) return;
    const steps = [...schema.steps];
    const [moved] = steps.splice(from, 1);
    steps.splice(to, 0, moved);
    setSchema((current) => ({ ...current, steps }));
    setStepIndex(to);
    setFieldKey(undefined);
  };

  const deleteStep = (index: number) => {
    if (schema.steps.length <= 1) return;
    const removed = schema.steps[index];
    const removedKeys = new Set(removed.fields.map((field) => field.key));
    const nextSteps = schema.steps.filter((_, i) => i !== index);
    setSchema((current) => {
      const cleaned = removeFieldReferences({ ...current, steps: nextSteps }, removedKeys);
      return cleaned;
    });
    setStepIndex(Math.min(index, nextSteps.length - 1));
    if (fieldKey && removedKeys.has(fieldKey)) setFieldKey(undefined);
    toast.success(`Deleted "${removed.title || `Step ${index + 1}`}"`);
  };

  const addField = () => {
    const used = new Set(allKeys.map((x) => x.key));
    let n = allKeys.length + 1;
    let key = `field_${n}`;
    while (used.has(key)) {
      n += 1;
      key = `field_${n}`;
    }
    const f = newField(n);
    f.key = key;
    f.label = `Field ${n}`;
    f.order = step.fields.length;
    setSchema((current) => ({
      ...current,
      steps: current.steps.map((item) => item.id === step.id
        ? { ...item, fields: [...item.fields, f].map((field, index) => ({ ...field, order: index })) }
        : item),
    }));
    setFieldKey(f.key);
  };

  const duplicateField = (source: FormField) => {
    const used = new Set(allKeys.map((x) => x.key));
    let n = 1;
    let key = `${source.key}_copy`;
    while (used.has(key)) {
      n += 1;
      key = `${source.key}_copy_${n}`;
    }
    const copy = {
      ...source,
      key,
      label: `${source.label} copy`,
      order: step.fields.length,
      options: source.options.map((o) => ({ ...o })),
      validations: source.validations.map((r) => ({ ...r })),
      reset_rules: source.reset_rules.map((r) => ({
        ...r,
        fields: [...r.fields],
      })),
    };
    setSchema((current) => ({
      ...current,
      steps: current.steps.map((item) => item.id === step.id
        ? { ...item, fields: [...item.fields, copy].map((field, index) => ({ ...field, order: index })) }
        : item),
    }));
    setFieldKey(key);
  };

  const duplicateStep = (sourceIndex: number) => {
    const source = schema.steps[sourceIndex];
    const usedStepIds = new Set(schema.steps.map((item) => item.id));
    let stepNumber = schema.steps.length + 1;
    let stepId = `${source.id}_copy`;
    while (usedStepIds.has(stepId)) {
      stepNumber += 1;
      stepId = `${source.id}_copy_${stepNumber}`;
    }

    const usedKeys = new Set(allKeys.map((item) => item.key));
    const keyMap: Record<string, string> = {};
    source.fields.forEach((item) => {
      let key = `${item.key}_copy`;
      let n = 1;
      while (usedKeys.has(key) || Object.values(keyMap).includes(key)) {
        n += 1;
        key = `${item.key}_copy_${n}`;
      }
      keyMap[item.key] = key;
    });

    const fields = source.fields.map((item, index) => ({
      ...remapFieldReferences(item, keyMap),
      key: keyMap[item.key],
      label: `${item.label || 'Untitled field'} copy`,
      order: index,
      options: item.options.map((option) => ({ ...option })),
      validations: item.validations.map((rule) => ({ ...rule })),
      reset_rules: item.reset_rules.map((rule) => ({ ...rule, fields: [...rule.fields] })),
    }));
    const copy: FormStep = {
      ...source,
      id: stepId,
      title: `${source.title || `Step ${sourceIndex + 1}`} copy`,
      fields,
    };
    const nextSteps = [...schema.steps];
    nextSteps.splice(sourceIndex + 1, 0, copy);
    setSchema((current) => ({ ...current, steps: nextSteps }));
    setStepIndex(sourceIndex + 1);
    setFieldKey(fields[0]?.key);
  };

  const moveFieldToStep = (sourceKey: string, targetIndex: number) => {
    const sourceIndex = schema.steps.findIndex((item) => item.fields.some((item) => item.key === sourceKey));
    if (sourceIndex < 0 || sourceIndex === targetIndex) return;
    const sourceStep = schema.steps[sourceIndex];
    const targetStep = schema.steps[targetIndex];
    const moved = sourceStep.fields.find((item) => item.key === sourceKey);
    if (!moved) return;

    const nextSteps = schema.steps.map((item) => ({ ...item, fields: [...item.fields] }));
    nextSteps[sourceIndex].fields = nextSteps[sourceIndex].fields
      .filter((item) => item.key !== sourceKey)
      .map((item, index) => ({ ...item, order: index }));
    nextSteps[targetIndex].fields = [
      ...nextSteps[targetIndex].fields,
      { ...moved, order: nextSteps[targetIndex].fields.length },
    ];

    setSchema((current) => ({ ...current, steps: nextSteps }));
    setStepIndex(targetIndex);
    setFieldKey(sourceKey);
    setMovingFieldKey(undefined);
    toast.success(`Moved "${moved.label || moved.key}" to "${targetStep.title || `Step ${targetIndex + 1}`}"`);
  };


  const deleteField = (key: string) => {
    const next = step.fields.filter((f) => f.key !== key).map((f, i) => ({ ...f, order: i }));
    setSchema((current) => {
      const nextSchema = {
        ...current,
        steps: current.steps.map((item, i) => i === stepIndex ? { ...item, fields: next } : item),
      };
      return removeFieldReferences(nextSchema, new Set([key]));
    });
    if (fieldKey === key) setFieldKey(undefined);
  };

  const updateField = (f: FormField) => {
    const selectedKey = fieldKey;
    if (!selectedKey) return;
    setSchema((current) => {
      const keyMap = selectedKey !== f.key ? { [selectedKey]: f.key } : {};
      const next = {
        ...current,
        steps: current.steps.map((item) => ({
          ...item,
          fields: item.fields.map((itemField) => itemField.key === selectedKey ? remapFieldReferences(f, keyMap) : itemField),
          dynamic_schema: remapFieldReferences({
  key: '__dynamic__',
  label: 'Runtime dynamic schema',
  type: 'dynamic_group',
  order: 0,
  options: [],
  validations: [],
  reset_rules: [],
  accept: [],
  multiple: false,
  data_source: item.dynamic_schema,
}, keyMap).data_source,
        })),
      };
      if (!Object.keys(keyMap).length) return next;
      const remapped = {
        ...next,
        steps: next.steps.map((item) => ({
          ...item,
          fields: item.fields.map((itemField) => itemField.key === f.key ? itemField : remapFieldReferences(itemField, keyMap)),
          dynamic_schema: remapFieldReferences({
  key: '__dynamic__',
  label: 'Runtime dynamic schema',
  type: 'dynamic_group',
  order: 0,
  options: [],
  validations: [],
  reset_rules: [],
  accept: [],
  multiple: false,
  data_source: item.dynamic_schema,
}, keyMap).data_source,
        })),
        submission: next.submission ? {
          ...next.submission,
          mappings: next.submission.mappings.map((mapping) => ({
            ...mapping,
            source: mapping.source.field && keyMap[mapping.source.field] ? { ...mapping.source, field: keyMap[mapping.source.field] } : mapping.source,
            when: remapCondition(mapping.when, keyMap),
          })),
          runtime_mappings: next.submission.runtime_mappings.map((mapping) => ({
            ...mapping,
            dynamic_group_key: mapping.dynamic_group_key ? (keyMap[mapping.dynamic_group_key] || mapping.dynamic_group_key) : mapping.dynamic_group_key,
          })),
          error_mappings: next.submission.error_mappings.map((mapping) => ({ ...mapping, field: mapping.field ? (keyMap[mapping.field] || mapping.field) : mapping.field })),
        } : next.submission,
      };
      return remapped;
    });
    if (selectedKey !== f.key) setFieldKey(f.key);
  };

  const applyFieldOrder = (nextOrder: number) => {
    if (!step || !fieldKey) return;
    const from = step.fields.findIndex((x) => x.key === fieldKey);
    if (from < 0) return;
    const to = Math.min(Math.max(nextOrder, 0), step.fields.length - 1);
    if (from === to) return;
    const fields = [...step.fields];
    const [moved] = fields.splice(from, 1);
    fields.splice(to, 0, moved);
    updateStep({ ...step, fields: fields.map((item, index) => ({ ...item, order: index })) });
  };

  const moveField = (from: number, to: number) => {
    if (from === to || to < 0 || to >= step.fields.length) return;
    const fields = [...step.fields];
    const [moved] = fields.splice(from, 1);
    fields.splice(to, 0, moved);
    updateStep({ ...step, fields: fields.map((f, i) => ({ ...f, order: i })) });
  };

  const persistDraft = async (showToast = true) => {
    if (!name.trim()) throw new Error('Form name is required');
    const normalized = normalizeSchema(schema);
    let targetId = activeFormId;

    if (targetId) {
      await formBuilderApi.update(targetId, { name, description, tags: tags.length ? tags : undefined, schema: normalized });
    } else {
      if (!sensorType || !category || !tags.length) throw new Error('Choose a sensor type, category and calibration type first');
      const created = await formBuilderApi.create({ name, description, sensor_type: sensorType, category, tags, schema: normalized });
      targetId = created.id;
      setPersistedFormId(targetId);
    }
    if (showToast) toast.success('Draft saved');
    return targetId;
  };

  const save = async () => {
    try {
      await persistDraft();
      if (!activeFormId) navigate(ROUTES.FORM_BUILDER);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || 'Save failed');
    }
  };

  const [publishOpen, setPublishOpen] = useState(false);
  const [publishNote, setPublishNote] = useState('');

  // Memoized: FormRenderer re-fetches dynamic schemas whenever this prop changes identity.
  const previewSchema = useMemo(() => (preview ? normalizeSchema(schema) : undefined), [preview, schema]);

  // Preview never saves: dropdowns/dynamic fields resolve against the unsaved schema. A brand-new
  // form has no id to resolve against yet, so it is created once (nothing is published to protect).
  const openPreview = async () => {
    try {
      if (!activeFormId) await persistDraft(false);
      setPreview(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || 'Unable to open preview');
    }
  };

  const openDuplicate = () => {
    if (!activeFormId) return;
    setDuplicateOpen(true);
  };

  const duplicateForm = async (selection: DuplicateSelection) => {
    if (!activeFormId) return;
    setDuplicating(true);
    try {
      await persistDraft(false);
      const created = await formBuilderApi.duplicate(activeFormId, {
        name: selection.name,
        description,
        sensor_type: selection.sensor_type,
        category: selection.category,
        tags: selection.tags,
      });
      toast.success('Form duplicated');
      navigate(ROUTES.formBuilderEdit(created.id));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || 'Duplicate failed');
    } finally {
      setDuplicating(false);
    }
  };

  const publish = async () => {
    if (!activeFormId) return;
    try {
      await persistDraft(false);
      await formBuilderApi.publish(activeFormId, publishNote.trim());
      setPublishOpen(false);
      setPublishNote('');
      toast.success('Form published');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || 'Publish failed');
    }
  };

  if (missingSelection) return <Navigate to={ROUTES.FORM_BUILDER} replace state={{ openNew: true }} />;
  if (loading) return <EditorSkeleton />;

  if (preview)
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden bg-slate-50">
        <header className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 py-3 md:px-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-600">
              <Eye className="h-3.5 w-3.5" /> Test preview
            </div>
            <h1 className="truncate text-base font-semibold text-slate-900">
              {name || 'Untitled form'}
            </h1>
            <p className="hidden text-[11px] text-slate-500 sm:block">
              Test validation, conditional logic and choices before publishing.
            </p>
          </div>
          <button className={secondaryButton} onClick={() => setPreview(false)}>
            <ArrowLeft className="h-3.5 w-3.5" /> Back to builder
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-auto px-4 py-5 md:px-8">
          <div className="mx-auto mb-4 flex max-w-4xl items-center gap-2 rounded-lg border border-primary-100 bg-primary-50 px-3 py-2 text-[11px] text-primary-800">
            <Eye className="h-4 w-4 shrink-0" />
            <span>
              <strong className="font-semibold">Interactive preview.</strong>{' '}
              Nothing is submitted to the production form.
            </span>
          </div>
          <div className="mx-auto max-w-4xl rounded-xl border border-slate-200 bg-white p-5 shadow-sm md:p-7">
            <FormRenderer
              schema={schema}
              onSubmit={(v) => {
                toast.success(
                  `Preview passed validation · ${Object.keys(v).length} values`,
                );
              }}
              runtimeFormId={activeFormId || undefined}
              dataSourceDraft={previewSchema}
              value={previewValues}
              onChange={setPreviewValues}
              initialStep={previewStep}
              onStepChange={setPreviewStep}
            />
          </div>
        </div>
      </div>
    );

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-slate-50">
      <EditorHeader
        editing={editing}
        title={name}
        sensorType={sensorType}
        category={category}
        tags={tags}
        onTagsChange={editing ? setTags : undefined}
        onCancel={() => navigate(ROUTES.FORM_BUILDER)}
        onVersions={() => navigate(ROUTES.formBuilderVersions(activeFormId || ''))}
        onAudit={() => navigate(ROUTES.formBuilderAudit(activeFormId || ''))}
        onPreview={openPreview}
        onSave={save}
        onPublish={() => setPublishOpen(true)}
      />

      <div className="grid min-h-0 flex-1 grid-cols-[270px_minmax(0,1fr)_minmax(360px,430px)] gap-3 overflow-hidden p-3">
        <aside className="min-h-0 space-y-3 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-3 shadow-sm">
          <SetupSection
            name={name}
            setName={setName}
            description={description}
            setDescription={setDescription}
            schema={schema}
            setSchema={setSchema}
          />
          <StepsSection
            schema={schema}
            stepIndex={stepIndex}
            dragStepIndex={dragStepIndex}
            setDragStepIndex={setDragStepIndex}
            stepMenuIndex={stepMenuIndex}
            setStepMenuIndex={setStepMenuIndex}
            onSelectStep={(i) => { setStepIndex(i); setFieldKey(undefined); }}
            onAddStep={addStep}
            onMoveStep={moveStep}
            onDuplicateStep={duplicateStep}
            onDeleteStep={deleteStep}
          />
          <ReviewSection schema={schema} setSchema={setSchema} />
          <SubmissionSection schema={schema} setSchema={setSchema} />
        </aside>

        <FieldListPanel
          schema={schema}
          step={step}
          stepIndex={stepIndex}
          fieldKey={fieldKey}
          allKeys={allKeys}
          sensorType={sensorType}
          category={category}
          dragKey={dragKey}
          setDragKey={setDragKey}
          movingFieldKey={movingFieldKey}
          setMovingFieldKey={setMovingFieldKey}
          onSelectField={setFieldKey}
          onAddField={addField}
          onUpdateStep={updateStep}
          onMoveField={moveField}
          onDuplicateField={duplicateField}
          onDeleteField={deleteField}
          onMoveFieldToStep={moveFieldToStep}
        />

        <aside data-tour="editor-field-config" className="min-h-0 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          {field ? (
            <FieldEditor
              field={field}
              allKeys={allKeys}
              onChange={updateField}
              onApplyOrder={applyFieldOrder}
              fieldCount={step?.fields.length || 1}
            />
          ) : (
            <div className="flex min-h-full flex-col items-center justify-center px-8 py-16 text-center">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary-50 text-primary-600">
                <Settings2 className="h-5 w-5" />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-slate-800">
                Select a field
              </h3>
              <p className="mt-1 max-w-xs text-[11px] leading-5 text-slate-500">
                Choose a field from the canvas to configure its type, options,
                validation and logic.
              </p>
            </div>
          )}
        </aside>
      </div>
      {duplicateOpen && (
        <DuplicateDialog
          initial={{ name: `${name || 'Form'} copy`, sensor_type: sensorType, category, tags }}
          busy={duplicating}
          onClose={() => setDuplicateOpen(false)}
          onSubmit={duplicateForm}
        />
      )}
      {publishOpen && (
        <PublishDialog
          note={publishNote}
          onNoteChange={setPublishNote}
          onClose={() => setPublishOpen(false)}
          onPublish={publish}
        />
      )}
    </div>
  );
}
